import type { Ref } from 'vue'

/**
 * A media query that is safe to drive markup with. Use it instead of
 * useDevice(): behind CloudFront the origin never receives the User-Agent
 * (see docs/infrastructure-cloudfront.md), so SSR always renders "desktop".
 *
 * - SSR: always `false`.
 * - While the component hydrates: `false` for the first render (matching SSR),
 *   then the real value after mount.
 * - Client-side mounts (navigation, v-if): the real value right away, so there
 *   is no second render.
 *
 * Must be called in a component's setup; throws otherwise.
 */
export const useHydrationSafeMediaQuery = (
  query: string
): Readonly<Ref<boolean>> => {
  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error(
      `useHydrationSafeMediaQuery('${query}') must be called inside a component's setup.`
    )
  }

  const matches = ref(false)
  if (import.meta.server) {
    return readonly(matches)
  }

  const mediaQueryList = window.matchMedia(query)
  const update = () => {
    matches.value = mediaQueryList.matches
  }

  // `nuxtApp.isHydrating` covers the initial page hydration. A component
  // hydrated later (lazy hydration, `hydrate-on-*`) runs setup after it is
  // false; Vue has already bound `vnode.el` to the server DOM by then, while a
  // fresh client mount has no element yet.
  const isHydrating = useNuxtApp().isHydrating || instance.vnode.el !== null
  if (isHydrating) {
    onMounted(update)
  } else {
    update()
  }

  mediaQueryList.addEventListener('change', update)
  onBeforeUnmount(() => {
    mediaQueryList.removeEventListener('change', update)
  })

  return readonly(matches)
}
